/**
 * ristretto255 group wrapper. Every scalar and point that crosses this module boundary is a
 * 32-byte Uint8Array, so callers never touch bigint point objects and the package's public
 * type surface stays independent of the underlying library.
 */
import { ristretto255 } from "@noble/curves/ed25519.js";
import { bytesToNumberLE } from "@noble/curves/utils.js";
import { randomBytes } from "@noble/hashes/utils.js";
const Point = ristretto255.Point;
const Fn = Point.Fn;
export const SCALAR_BYTES = 32;
export const POINT_BYTES = 32;
/** Prime order of the ristretto255 group (same as the ed25519 subgroup order). */
export const ORDER = Fn.ORDER;
export function randomScalar() {
    // Reduce 64 uniformly random bytes to get a scalar statistically indistinguishable from uniform.
    let s = 0n;
    do {
        s = Fn.create(bytesToNumberLE(randomBytes(64)));
    } while (s === 0n);
    return s;
}
export function scalarToBytes(s) {
    return Fn.toBytes(Fn.create(s));
}
export function scalarFromBytes(b) {
    if (b.length !== SCALAR_BYTES)
        throw new Error("scalar must be 32 bytes");
    return Fn.fromBytes(b);
}
export const scalar = {
    add: (a, b) => Fn.add(a, b),
    sub: (a, b) => Fn.sub(a, b),
    mul: (a, b) => Fn.mul(a, b),
    inv: (a) => Fn.inv(a),
    neg: (a) => Fn.neg(a),
    create: (a) => Fn.create(a),
};
function decode(b) {
    if (b.length !== POINT_BYTES)
        throw new Error("point must be 32 bytes");
    return Point.fromBytes(b);
}
/** Encoded identity element. */
export function pointZero() {
    return Point.ZERO.toBytes();
}
export function pointAdd(a, b) {
    return decode(a).add(decode(b)).toBytes();
}
/** s·G, constant-time; tolerates a zero scalar. */
export function basePointMul(s) {
    if (s === 0n)
        return pointZero();
    return Point.BASE.multiply(s).toBytes();
}
/** s·P, constant-time; tolerates a zero scalar. Validates the encoding of P. */
export function pointMul(p, s) {
    const P = decode(p);
    if (s === 0n)
        return pointZero();
    return P.multiply(s).toBytes();
}
/** Validates that bytes are a canonical ristretto255 encoding. */
export function assertPoint(b) {
    decode(b);
}
export function generateGroupKeypair() {
    const s = randomScalar();
    return { secretKey: scalarToBytes(s), publicKey: basePointMul(s) };
}
export function publicKeyFromSecret(secretKey) {
    return basePointMul(scalarFromBytes(secretKey));
}
//# sourceMappingURL=group.js.map